import { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import SwitchToggle from "../../../components/SwitchToggle";
import AddIcon from '@mui/icons-material/Add';
import SearchBar from "../../../components/SearchBar";
import AddEditAlertConfig from "../../../Modals/addAlertConfig";
import { useDeleteAlertConfigMutation, useLazyGetAlertConfigQuery } from "../../../services/alertConfig";
import { useLazyGetBudgetCategoryQuery } from "../../../services/budgetCategory";
import { showError, showToast } from "../../../constants/toasts";
import DeleteConfirmModal from "../../../Modals/deleteConfirmModal";
import IfCan from "../../../components/IfCan";
const AlertList = () => {
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [getAlertConfig] = useLazyGetAlertConfigQuery()
  const [getBudgetCategory] = useLazyGetBudgetCategoryQuery()
  const [deleteAlert] = useDeleteAlertConfigMutation()
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [categoryMap, setCategoryMap] = useState<{ [key: number]: string }>({});
  const totalPages = Math.ceil(totalCount / 10);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const handleCloseModal = () => {
    setOpen(false);
    setEditId(null);
  };

  const handleEdit = (id: number) => {
    setEditId(id);
    setOpen(true);
  };

  const handleAdd = () => {
    setEditId(null);
    setOpen(true);
  };

  const handleSuccess = () => {
    getAlertList();
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    try {
      await deleteAlert({ id: selectedId }).unwrap();
      showToast("Alert configuration deleted successfully");
      getAlertList();
    } catch (e: any) {
      showError(e?.data?.message || "Failed to delete alert configuration");
    }
    setDeleteOpen(false);
    setSelectedId(null);
  };

  const getBudgetCategories = async () => {
    try {
      const response = await getBudgetCategory({
        search: "",
        start: 1,
        length: 1000
      }).unwrap();

      if (response?.status === 200) {
        const categories = response?.data?.response_object || [];
        const map: { [key: number]: string } = {};
        categories.forEach((category: any) => {
          map[category.id] = category.category_name;
        });
        setCategoryMap(map);
      }
    } catch (error) {
      console.log("Error fetching budget categories:", error);
    }
  };

  const getAlertList = async () => {
    setIsLoading(true);
    try {
      const response = await getAlertConfig({
        search: debouncedSearchTerm,
        start: page,
        length: 10
      }).unwrap();

      if (response?.status === 200) {
        setData(response?.data?.response_object)
        setTotalCount(response?.data?.total_records || 0)

      } else {
        setData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.log(error);
      setData([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getBudgetCategories();
    getAlertList();
  }, [page, debouncedSearchTerm])

  return (
    <>
      <div className="table_header">
        <div className="left_s">
          <Box className="search_box">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="Search..."
            />
          </Box>
        </div>
        <div className="right_s">
          <IfCan module="Budget Intelligence" action="add_edit">
            <Button className="btn btn_primary" onClick={handleAdd}><AddIcon /> Add Alert</Button>
          </IfCan>
        </div>
      </div>
      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Category Name</TableCell>
              <TableCell>Customize Nudge Message</TableCell>
              <TableCell>Smart Forecasting</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
                  Loading...
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
                  No alert configurations found
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow hover key={row.id}>
                  <TableCell>
                    <div className="user_block">
                      <figure className="no_radius">
                        <img src="/static/images/placeholder.png" alt="Category" />
                      </figure>
                      <p><b>{categoryMap[row.budget_category] || 'Unknown Category'}</b></p>
                    </div>
                  </TableCell>
                  <TableCell>{row.nudge_message}</TableCell>
                  <TableCell>
                    <IfCan module="Budget Intelligence" action="add_edit">
                      <SwitchToggle />
                    </IfCan>
                  </TableCell>
                  <TableCell>
                    <Box className="table_actions v2">
                      <IfCan module="Budget Intelligence" action="add_edit">
                        <IconButton onClick={() => handleEdit(row?.id)}>
                          <img src="/static/images/edit2_icon.svg" alt="Edit" />
                        </IconButton>
                      </IfCan>
                      <IfCan module="Budget Intelligence" action="delete">
                        <IconButton onClick={() => { setSelectedId(row?.id); setDeleteOpen(true); }}>
                          <img src="/static/images/trash2_icon.svg" alt="Delete" />
                        </IconButton>
                      </IfCan>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <AddEditAlertConfig
        open={open}
        onClose={handleCloseModal}
        setOpen={setOpen}
        mode={editId ? "edit" : "add"}
        editId={editId}
        onSuccess={handleSuccess}
      />
      <DeleteConfirmModal
        open={deleteOpen}
        setOpen={setDeleteOpen}
        handleDelete={handleDelete}
        title="Are you sure you want to delete this alert configuration?"
        message="This action cannot be undone."
      />
    </>
  );
};

export default AlertList;
